import React, { useState, useEffect } from 'react';
import { ChevronRight, ArrowLeft, ShoppingBag, CheckCircle, Loader2, RefreshCw } from 'lucide-react';

// ==========================================
// 🔴 Dify API 設定 🔴
// ==========================================
const DIFY_API_KEY = "app-UHll9VDpartFdSj2YHP2NtGp"; 
const DIFY_API_URL = "https://api.dify.ai/v1";
// ==========================================

// --- 共通フォールバック画像 (エラー回避のため大文字定数で統一) ---
const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1596462502278-27bfdd403348?auto=format&fit=crop&q=80&w=800&h=800",
  "https://images.unsplash.com/photo-1620331311520-246422fd82f9?auto=format&fit=crop&q=80&w=800&h=800",
  "https://images.unsplash.com/photo-1582236595507-d7db913847e3?auto=format&fit=crop&q=80&w=800&h=800"
];

// --- スタイル別 URL 設定 (重複なし) ---
const STYLE_URLS = {
  luxury: [
    "https://lg.houkyou-beauty.com/?p=84",
    "https://lg.houkyou-beauty.com/?p=37"
  ],
  feminine: [
    "https://lg.houkyou-beauty.com/?p=80",
    "https://lg.houkyou-beauty.com/?p=19"
  ],
  natural: [
    "https://lg.houkyou-beauty.com/?p=75",
    "https://lg.houkyou-beauty.com/?p=47"
  ],
  default: "https://lg.houkyou-beauty.com/"
};

const styles = {
  appContainer: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
    boxSizing: 'border-box',
  },
  mobileWrapper: {
    width: '100%',
    maxWidth: '420px',
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    padding: '16px 20px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  grid3x3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)', 
    gap: '10px',
    padding: '0',
    boxSizing: 'border-box',
    width: '100%',
  },
  squareBtn: {
    aspectRatio: '1 / 1', 
    width: '100%',
    borderRadius: '16px',
    border: '2px solid',
    backgroundColor: 'white',
    padding: '0',
    overflow: 'hidden',
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
  },
  nextButton: {
    backgroundColor: '#111827',
    color: 'white',
    padding: '14px 32px',
    borderRadius: '9999px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
  },
  secondaryButton: {
    backgroundColor: 'white',
    color: '#374151',
    padding: '12px 24px',
    borderRadius: '9999px',
    fontWeight: 'bold',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: '1px solid #d1d5db',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputField: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    fontSize: '18px',
    fontWeight: 'bold',
    textAlign: 'center',
    outline: 'none',
    backgroundColor: '#f9fafb',
    boxSizing: 'border-box',
  }
};

const GlobalStyle = () => (
  <style>
    {`
      *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        -webkit-font-smoothing: antialiased;
      }
      body {
        overflow-x: hidden;
      }
      input, textarea, button {
        font-family: inherit;
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-spin {
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}
  </style>
);

const ShapeIcon = ({ id }) => {
  const imagePath = `/${id}.png`; 
  return (
    <img 
      src={imagePath} 
      alt={`Shape ${id}`} 
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = `https://placehold.co/200x200/ffffff/db2777?text=Shape+${id}`; 
      }}
    />
  );
};

export default function App() {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0); // 修正: プログレス用のステートを復活
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const initialInputs = {
    age: '',
    topBust: '',
    underBust: '',
    shape: null, 
    preferences: ''
  };

  const [inputs, setInputs] = useState(initialInputs);
  
  const [apiResult, setApiResult] = useState({
    size: "",
    comments: [],
    images: [],
    baseImage: null
  });

  useEffect(() => {
    document.title = "AI試着シミュレーション";
  }, []);

  const handleImageError = (e) => {
    e.target.onerror = null; 
    e.target.src = PLACEHOLDERS[0];
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // 修正: 読み込み中のプログレスバーアニメーションロジックを復活
  useEffect(() => {
    let interval;
    if (isLoading) {
      setLoadingProgress(0);
      interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 98) return 98; // 完了までは98%で止める
          const increment = Math.random() * 8;
          return Math.min(prev + increment, 98);
        });
      }, 800);
    } else {
      setLoadingProgress(0);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleReset = () => {
    setInputs(initialInputs);
    setApiResult({
      size: "",
      comments: [],
      images: [],
      baseImage: null
    });
    setStep(1);
  };

  const getBranchedUrl = (commentText) => {
    if (!commentText) return STYLE_URLS.default;

    const lowerText = commentText.toLowerCase();
    let targetUrls = [];

    if (lowerText.includes("luxury") || lowerText.includes("ラグジュアリー")) {
      targetUrls = STYLE_URLS.luxury;
    } else if (lowerText.includes("feminine") || lowerText.includes("フェミニン")) {
      targetUrls = STYLE_URLS.feminine;
    } else if (lowerText.includes("natural") || lowerText.includes("ナチュラル")) {
      targetUrls = STYLE_URLS.natural;
    } else {
      return STYLE_URLS.default;
    }

    const randomIndex = Math.floor(Math.random() * targetUrls.length);
    return targetUrls[randomIndex];
  };

  const extractImageUrl = (imageOutput) => {
    if (!imageOutput) return null;
    if (Array.isArray(imageOutput) && imageOutput.length > 0) {
       if (imageOutput[0].url) return imageOutput[0].url;
       if (typeof imageOutput[0] === 'string') return imageOutput[0];
    }
    if (typeof imageOutput === 'object' && imageOutput.url) return imageOutput.url;
    if (typeof imageOutput === 'string') {
      const match = imageOutput.match(/\((https?:\/\/[^)]+)\)/);
      if (match && match[1]) return match[1];
      if (imageOutput.startsWith('http')) return imageOutput.trim();
    }
    return null;
  };

  const calculateSizeLocally = () => {
    const top = parseInt(inputs.topBust) || 0;
    const under = parseInt(inputs.underBust) || 0;
    const diff = top - under;
    let cup = 'A';
    if (diff >= 25) cup = 'G'; else if (diff >= 22.5) cup = 'F'; else if (diff >= 20) cup = 'E';
    else if (diff >= 17.5) cup = 'D'; else if (diff >= 15) cup = 'C'; else if (diff >= 12.5) cup = 'B';
    else if (diff >= 10) cup = 'A'; else cup = 'AA';
    const roundUnder = Math.round(under / 5) * 5;
    return `${cup}${roundUnder}`;
  };

  const callDifyAPI = async () => {
    setIsLoading(true);
    setSelectedImageIndex(null); 

    if (DIFY_API_KEY === "app-xxxxxxxxxxxxxxxxxxxxxxxx" || !DIFY_API_KEY) {
      setTimeout(() => {
        setApiResult({
          size: calculateSizeLocally(),
          comments: [
            "【Style 1: Natural】\n肌馴染みの良いヌーディーカラーとシームレスなデザインで、日常使いに最適です。",
            "【Style 2: Feminine】\n繊細なレースを贅沢に使用したフェミニンなデザイン。",
            "【Style 3: Luxury】\n光沢のあるサテン生地とゴールドのアクセントが高級感を演出。"
          ],
          images: [
            PLACEHOLDERS[0],
            PLACEHOLDERS[1],
            PLACEHOLDERS[2]
          ],
          baseImage: PLACEHOLDERS[0]
        });
        setIsLoading(false);
        setStep(4);
      }, 3000);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(`${DIFY_API_URL}/workflows/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${DIFY_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inputs: {
            age: parseInt(inputs.age) || 25, 
            top_bust: parseInt(inputs.topBust) || 0,
            under_bust: parseInt(inputs.underBust) || 0,
            shape: inputs.shape,
            preferences: inputs.preferences
          },
          response_mode: "blocking",
          user: "user-" + Math.random().toString(36).substring(7)
        }),
        signal: controller.signal 
      });
      
      clearTimeout(timeoutId); 

      if (!response.ok) throw new Error(`API Error (${response.status})`);

      const data = await response.json();
      const outputs = data.data.outputs;

      const images = [];
      if (outputs.image1) images.push(extractImageUrl(outputs.image1));
      if (outputs.image2) images.push(extractImageUrl(outputs.image2));
      if (outputs.image3) images.push(extractImageUrl(outputs.image3));
      
      const baseImage = extractImageUrl(outputs.baseimage) || extractImageUrl(outputs.base_image);

      const validImages = images.filter(url => url && typeof url === 'string' && url.length > 5);

      const finalImages = [...validImages];
      while (finalImages.length < 3) {
        finalImages.push(PLACEHOLDERS[finalImages.length % PLACEHOLDERS.length]);
      }

      const rawText = outputs.text || outputs.advice || outputs.result || "";
      let commentArray = rawText.split('///').map(s => s.trim()).filter(s => s.length > 0);
      
      if (commentArray.length === 0) {
        commentArray = ["コメントの取得に失敗しました。", "コメントの取得に失敗しました。", "コメントの取得に失敗しました。"];
      } else if (commentArray.length < 3) {
        while(commentArray.length < 3) commentArray.push(commentArray[commentArray.length - 1]);
      }

      setApiResult({
        size: outputs.size || outputs.result || calculateSizeLocally(),
        comments: commentArray, 
        images: finalImages,
        baseImage: baseImage
      });

      setStep(4);

    } catch (error) {
      clearTimeout(timeoutId);
      console.error("Fetch Error:", error);
      
      setApiResult({
        size: calculateSizeLocally(),
        comments: ["【Style 1: Natural】Demo", "【Style 2: Feminine】Demo", "【Style 3: Luxury】Demo"],
        images: [PLACEHOLDERS[0], PLACEHOLDERS[1], PLACEHOLDERS[2]],
        baseImage: PLACEHOLDERS[0]
      });
      setStep(4);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: '0 0 8px 0' }}>AIバスト測定</h1>
        <p style={{ color: '#6b7280', fontSize: '13px' }}>年齢と体型に合わせた最適解を</p>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '24px 20px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', textAlign: 'center', color: '#1f2937' }}>年齢</label>
            <input 
              type="number" 
              value={inputs.age} 
              onChange={(e) => setInputs({...inputs, age: e.target.value})} 
              style={styles.inputField}
              placeholder="25"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4b5563', marginBottom: '8px', textAlign: 'center' }}>トップ (cm)</label>
              <input 
                type="number" 
                value={inputs.topBust} 
                onChange={(e) => setInputs({...inputs, topBust: e.target.value})} 
                style={styles.inputField}
                placeholder="85"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4b5563', marginBottom: '8px', textAlign: 'center' }}>アンダー (cm)</label>
              <input 
                type="number" 
                value={inputs.underBust} 
                onChange={(e) => setInputs({...inputs, underBust: e.target.value})} 
                style={styles.inputField}
                placeholder="70"
              />
            </div>
          </div>
        </div>
      </div>
      
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center', color: '#1f2937' }}>胸の形を選択</label>
        <div style={styles.grid3x3}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => (
            <button
              key={id}
              onClick={() => setInputs({...inputs, shape: id})}
              style={{
                ...styles.squareBtn,
                borderColor: inputs.shape === id ? '#ec4899' : '#e5e7eb',
                boxShadow: inputs.shape === id ? '0 4px 6px -1px rgba(236, 72, 153, 0.3)' : 'none',
                transform: inputs.shape === id ? 'scale(1.02)' : 'scale(1)',
                zIndex: inputs.shape === id ? 10 : 1,
              }}
            >
              <ShapeIcon id={id} />
              {inputs.shape === id && (
                <div style={{
                  position: 'absolute', top: '6px', right: '6px', width: '20px', height: '20px',
                  backgroundColor: '#ec4899', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <CheckCircle size={12} color="white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ paddingTop: '8px', paddingBottom: '32px', display: 'flex', justifyContent: 'center' }}>
        <button 
          onClick={() => setStep(2)} 
          disabled={!inputs.shape || !inputs.topBust || !inputs.underBust}
          style={{
            ...styles.nextButton,
            opacity: (!inputs.shape || !inputs.topBust || !inputs.underBust) ? 0.5 : 1,
          }}
        >
          診断する <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '32px', textAlign: 'center', paddingTop: '40px' }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>あなたの推定サイズ</h2>
        <div style={{ marginTop: '16px', display: 'inline-block', backgroundColor: 'white', padding: '24px 48px', borderRadius: '24px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', border: '1px solid #fce7f3' }}>
           <span style={{ fontSize: '48px', fontWeight: '900', color: '#db2777' }}>{apiResult.size || calculateSizeLocally()}</span>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ border: '1px solid #fbcfe8', backgroundColor: '#fdf2f8', borderRadius: '16px', padding: '16px', width: '160px' }}>
          <div style={{ width: '100%', aspectRatio: '1/1', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden' }}>
            {inputs.shape && <ShapeIcon id={inputs.shape} />}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <button onClick={() => setStep(3)} style={styles.nextButton}>AIシミュレーション詳細へ <ChevronRight size={20} /></button>
        <button onClick={() => setStep(1)} style={{ color: '#9ca3af', fontSize: '14px', background: 'none', border: 'none', textDecoration: 'underline' }}>数値を修正する</button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button onClick={() => setStep(2)} style={{ padding: '8px', background: 'none', border: 'none' }}><ArrowLeft color="#6b7280" /></button>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>AIへの相談</h2>
      </div>
      <textarea 
        style={{ ...styles.inputField, height: '180px', textAlign: 'left', fontSize: '16px', padding: '16px' }} 
        placeholder="例：脇肉をすっきりさせたい、レースデザインが好き..." 
        value={inputs.preferences} 
        onChange={(e) => setInputs({...inputs, preferences: e.target.value})}
      />
      
      {/* 修正: 読み込み中にピンクのゲージ（プログレスバー）を表示するようにボタンを更新 */}
      <button 
        onClick={callDifyAPI} 
        disabled={!inputs.preferences || isLoading}
        style={{ ...styles.nextButton, width: '100%', justifyContent: 'center', height: '60px', overflow: 'hidden' }}
      >
        {isLoading ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'bold' }}>
              <Loader2 className="animate-spin" size={16} />
              AI試着イメージを作成中... {Math.round(loadingProgress)}%
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: 'rgba(255, 255, 255, 0.2)', borderRadius: '99px', overflow: 'hidden' }}>
              <div 
                style={{ 
                  height: '100%', 
                  width: `${loadingProgress}%`, 
                  backgroundColor: '#ec4899', 
                  transition: 'width 0.4s ease-out',
                  borderRadius: '99px'
                }} 
              />
            </div>
          </div>
        ) : "シミュレーション開始"}
      </button>
    </div>
  );

  const renderStep4 = () => {
    const baseImageUrl = apiResult.baseImage || (inputs.shape ? `/${inputs.shape}.png` : null);
    return (
      <div style={{ paddingBottom: '80px' }}>
         <div style={{ padding: '24px 20px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>バーチャル試着結果</h2>
         </div>
         <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {baseImageUrl && (
               <div style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' }}>
                  <img src={baseImageUrl} style={{ width: '100%', height: 'auto', display: 'block' }} alt="Base" onError={handleImageError} />
                  <div style={{ padding: '12px', backgroundColor: '#f9fafb', fontSize: '12px', fontWeight: 'bold', textAlign: 'center' }}>現在の体型イメージ</div>
               </div>
            )}
            
            {apiResult.images.map((imgUrl, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <div 
                  onClick={() => setSelectedImageIndex(selectedImageIndex === idx ? null : idx)}
                  style={{ borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', cursor: 'pointer', border: selectedImageIndex === idx ? '3px solid #ec4899' : 'none' }}
                >
                  <img src={imgUrl} style={{ width: '100%', height: 'auto', display: 'block' }} alt={`Fit ${idx}`} onError={handleImageError} />
                  <div style={{ position: 'absolute', bottom: '16px', left: '16px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '99px', fontSize: '12px' }}>
                    Style {idx + 1}
                  </div>
                </div>
                {selectedImageIndex === idx && (
                  <div style={{ marginTop: '12px', padding: '16px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #f3f4f6', animation: 'fadeIn 0.3s' }}>
                    <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>{apiResult.comments[idx]}</p>
                    <button onClick={() => setStep(5)} style={{ ...styles.nextButton, width: '100%', marginTop: '16px', justifyContent: 'center' }}>
                      詳しく見る <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </div>
            ))}
         </div>
         <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <button onClick={handleReset} style={{ color: '#9ca3af', fontSize: '14px', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', margin: '0 auto' }}>
              <RefreshCw size={14}/> 最初からやり直す
            </button>
         </div>
      </div>
    );
  };

  const renderStep5 = () => {
    const idx = selectedImageIndex !== null ? selectedImageIndex : 0;
    const displayImage = apiResult.images[idx];
    const displayComment = apiResult.comments[idx];
    const buyUrl = getBranchedUrl(displayComment);

    return (
      <div style={{ paddingBottom: '80px' }}>
        <div style={{ padding: '24px 20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
           <button onClick={() => setStep(4)} style={{ padding: '8px', background: 'none', border: 'none' }}><ArrowLeft color="#6b7280" /></button>
           <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>フィッティング詳細</h2>
        </div>

        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
           <img src={displayImage} style={{ width: '100%', borderRadius: '24px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)' }} alt="Fit" onError={handleImageError} />
           
           <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
             <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>AI解説</h3>
             <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{displayComment}</p>
             <div style={{ marginTop: '20px', backgroundColor: '#fdf2f8', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <span style={{ fontWeight: 'bold' }}>推奨サイズ</span>
               <span style={{ fontSize: '24px', fontWeight: '900', color: '#db2777' }}>{apiResult.size || calculateSizeLocally()}</span>
             </div>
           </div>
        </div>

        <div style={{ padding: '24px 20px' }}>
          <button 
            onClick={() => window.open(buyUrl, '_blank')} 
            style={{ ...styles.nextButton, width: '100%', justifyContent: 'center', backgroundColor: '#ec4899' }}
          >
            公式サイトで購入 <ShoppingBag size={18} />
          </button>
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '11px', marginTop: '12px' }}>
            ※スタイルに最適なアイテムのページをご案内しています
          </p>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.appContainer}>
      <GlobalStyle />
      <div style={styles.mobileWrapper}>
        <div style={styles.header}>
           <div style={{ fontSize: '18px', fontWeight: 'bold' }}>AI試着シミュレーション</div>
           <ShoppingBag size={20} color="#4b5563" onClick={() => window.open(STYLE_URLS.default, '_blank')} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </div>
      </div>
    </div>
  );
}